import { ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import type { ApprovalDecision, MachineProposal } from "../data/cockpit-data";
import { NotesBox } from "./NotesBox";
import { ProposalRow } from "./ProposalRow";
import { StatsBand } from "./StatsBand";

const TERMINAL: ReadonlyArray<MachineProposal["status"]> = [
  "approved",
  "rejected",
  "executed",
  "executing",
  "withdrawn",
];

/**
 * One proposal, with the decision-critical read always visible and the
 * reasoning behind a disclosure.
 *
 * The split is deliberate: the collapsed card carries everything a reviewer
 * needs to APPROVE (what, where, why in one line, and the buttons); the panels
 * that explain the machine's arithmetic are for the reviewer who wants to
 * argue with it. Showing all of it always turned a queue of 108 into a queue
 * nobody could scan.
 *
 * A decided proposal compresses to a single history row — no detail band, no
 * notes. It is a record, not work.
 */
export function ProposalCard({
  proposal,
  onDecided,
  onNoteSaved,
  campaignTemplateDefault,
  campaignTemplates,
}: {
  proposal: MachineProposal;
  onDecided?: (proposalId: string, decision: ApprovalDecision) => void;
  onNoteSaved?: () => void;
  campaignTemplateDefault?: string | null;
  campaignTemplates?: string[] | null;
}) {
  const [expanded, setExpanded] = useState(false);

  if (TERMINAL.includes(proposal.status)) {
    return (
      <Card>
        <CardContent className="py-1">
          <ProposalRow
            proposal={proposal}
            riskConfirm
            showReason={false}
            onDecided={onDecided}
            campaignTemplateDefault={campaignTemplateDefault}
            campaignTemplates={campaignTemplates}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        {/* The card IS the detail view, so profit-risk approvals confirm inline
            here (riskConfirm) instead of pointing at a detail page that never existed. */}
        <ProposalRow
          proposal={proposal}
          riskConfirm
          reasonClamped={!expanded}
          onExpandReason={() => setExpanded(true)}
          onDecided={onDecided}
          campaignTemplateDefault={campaignTemplateDefault}
          campaignTemplates={campaignTemplates}
        />
        {expanded && (
          <div className="flex flex-col gap-3">
            <StatsBand proposal={proposal} />
            <NotesBox proposal={proposal} onSaved={onNoteSaved} />
          </div>
        )}
        <Button
          variant="ghost"
          size="xs"
          className="self-start"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? <ChevronUp aria-hidden="true" /> : <ChevronDown aria-hidden="true" />}
          {expanded ? "Hide details" : "Show details"}
        </Button>
      </CardContent>
    </Card>
  );
}
